// Punktetafel.jsx — Tafel für jeden Kampf mit Punktestand am Mattenbildschirm
// (/display/:matNr?t=, pages/MatDisplay.jsx), 20.10.2026.
//
// Vorlage: docs/muster-animationen/muster.html, Seite „Finaltafel + Infos“
// (vom Veranstalter abgenommen). Bilder, Felderlagen (Pixel im 1672x941-Bild),
// Takte und Tempo (TEMPO_TAFEL 1,8) sind von dort übernommen.
//
// Aufteilung: React zeichnet das Gerüst und alle Texte, die sich nur mit dem
// Kampf ändern (Namen, Verein, Runde, Uhr). Punktzahlen, Lage der Felder,
// Verwarnungspunkte, Minuspunkt-Zähler und die Laufzeile „Letzte Wertung“
// führt der Ablauf (TafelAblauf) selbst am DOM, weil sie mitten in einer
// Animation wechseln. React rendert diese Knoten leer und fasst sie danach
// nicht mehr an.
//
// Was passiert ist, liest der Ablauf aus dem Unterschied zweier Stände
// (Punkte, Verwarnungen, Minuspunkte je Seite) — so passt die Anzeige auch,
// wenn ein Ereignis fehlt oder doppelt kommt. Der Stand ist schon nach Abzug
// der Minuspunkte (backend/utils/minuspunkte.js).
//
// prefers-reduced-motion: alle Zeiten 0, es steht sofort der Endstand.
import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { standText, autoDqText } from '../../lib/minuspunkte';
import { tafelVariablen } from '../../lib/seitenFarben';
import './Punktetafel.css';

const TEMPO_TAFEL = 1.8;
const BILDER = `${process.env.PUBLIC_URL || ''}/turniertag`;
const BW = 1672, BH = 941;
// Rahmen im Bild vermessen (Pixel im 1672x941-Bild, Goldkante innen), aus dem Muster
const SLOTS = {
  // Führungsbild tafel_a: oben groß, unten klein
  fuehrung: {
    oben:  { foto: [109, 267, 314, 471], name: [325, 294, 858, 448], pk: [881, 292, 1051, 461] },
    unten: { foto: [115, 562, 274, 722], name: [284, 582, 716, 703], pk: [736, 580, 871, 712] },
  },
  // Anfangsbild tafel_gleich: beide gleich groß (Gleichstand)
  gleich: {
    oben:  { foto: [111, 287, 303, 456], name: [320, 300, 848, 443], pk: [872, 287, 1048, 456] },
    unten: { foto: [111, 531, 302, 699], name: [320, 544, 848, 687], pk: [872, 531, 1048, 699] },
  },
};
const SCHRIFT = { foto: 0.36, name: 0.28, pk: 0.62 };
const SEITEN = ['rot', 'blau'];
const TAG = { rot: 'ROT', blau: 'BLAU' };
// Technik der letzten Wertung: seit 21.10.2026 schickt die Kampfrichter-App
// sie mit dem Knopf (backend/utils/kampfTechniken.js, Server prüft und
// reicht sie als letzte_wertung durch). Nur wenn keine mitkommt (ältere
// Tablets, Kumite-Bedienung bei Pointfighting), gilt weiter die ANNAHME
// (abgenommen 20.10.2026): Technik aus der Punktzahl abgeleitet.
const TECHNIK = { 1: 'Fauststoß', 2: 'Fußtritt zum Körper', 3: 'Fußtritt zum Kopf' };
// Kurzform nur für die Laufzeile; die Knöpfe der App behalten die langen Namen
const laufzeileKurz = (t) => String(t).replace(/^Fußtritt zum /, 'Tritt ');

const andere = (k) => (k === 'rot' ? 'blau' : 'rot');
const zahl = (v) => { const n = Number(v); return Number.isFinite(n) ? Math.trunc(n) : 0; };

/** Stand einer Matte → { rot: {stand, verw, minus}, blau: {...} } */
export function standAus(s) {
  const lw = s?.letzte_wertung;
  return {
    // letzte Wertung mit Technik aus dem Knopf: { farbe, punkte, technik, nr }
    lw: lw && lw.farbe ? { farbe: lw.farbe, punkte: zahl(lw.punkte), technik: lw.technik || null, nr: zahl(lw.nr) } : null,
    rot: { stand: zahl(s?.rote_punkte), verw: zahl(s?.rote_warnings), minus: zahl(s?.rote_minus) },
    blau: { stand: zahl(s?.blaue_punkte), verw: zahl(s?.blaue_warnings), minus: zahl(s?.blaue_minus) },
  };
}

function reduziert() {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
}

// ════════════════════════════════════════════════════════════════════════
//  Ablauf: Warteschlange der Animationen (wie bPunkt/blauf im Muster)
// ════════════════════════════════════════════════════════════════════════
class TafelAblauf {
  constructor(bt, namen, schilder) {
    this.bt = bt;
    this.bfx = bt.querySelector('.pt-bfx');
    this.namen = namen; // () => ({ rot, blau })
    this.schilder = schilder; // () => ({ rot: 'ROT', blau: 'BLAU' } oder die Beschriftung aus der Modus-Übersicht)
    this.tempo = reduziert() ? 0 : TEMPO_TAFEL;
    this.nr = 0;
    this.lauf = Promise.resolve();
    this.bmodus = 'gleich';
    this.boben = 'rot';
    this.bstand = { rot: 0, blau: 0 };
    this.strafen = { rot: 0, blau: 0 };
    this.minus = { rot: 0, blau: 0 };
    this.geplant = null; // letzter angenommener Stand (standAus)
    this.schild = null;  // „Unentschieden“/„Verlängerung“
    this.siegerGezeigt = false;
  }

  // ── Hilfen ───────────────────────────────────────────
  warte(ms) { return new Promise((r) => setTimeout(r, ms * this.tempo)); }
  anim(el, frames, o) {
    const opt = typeof o === 'number' ? { duration: o } : { ...(o || {}) };
    opt.duration = (opt.duration || 0) * this.tempo;
    if (opt.delay) opt.delay *= this.tempo;
    return el.animate(frames, opt);
  }
  el(k, t) { return this.bt.querySelector(`.pt-bx[data-k="${k}"][data-t="${t}"]`); }
  rel(el) {
    const t = this.bt.getBoundingClientRect(), r = el.getBoundingClientRect();
    return { x: r.left - t.left, y: r.top - t.top, w: r.width, h: r.height };
  }
  cq() { return this.bt.getBoundingClientRect().width / 100; }
  einreihen(f) {
    const nr = this.nr;
    this.lauf = this.lauf.then(() => (nr === this.nr ? f() : null)).catch((e) => { console.error('[Punktetafel]', e); });
    return this.lauf;
  }
  beenden() { this.nr += 1; this.bfx.innerHTML = ''; }

  // ── Felder setzen (Muster: setzen/bAufstellen) ───────
  setzen(k, platz) {
    ['foto', 'name', 'pk'].forEach((t) => {
      const [x0, y0, x1, y1] = SLOTS[this.bmodus][platz][t];
      const e = this.el(k, t), i = t === 'name' ? 0 : 14;
      Object.assign(e.style, {
        left: (x0 + i) / BW * 100 + '%', top: (y0 + i) / BH * 100 + '%',
        width: (x1 - x0 - 2 * i) / BW * 100 + '%', height: (y1 - y0 - 2 * i) / BH * 100 + '%',
        fontSize: `${(y1 - y0) * SCHRIFT[t] / BW * 100}cqw`,
      });
    });
  }
  aufstellen() { this.setzen(this.boben, 'oben'); this.setzen(andere(this.boben), 'unten'); }
  fuehrtModus() {
    const { rot, blau } = this.bstand;
    return { fuehrt: rot === blau ? this.boben : (rot > blau ? 'rot' : 'blau'), modus: rot === blau ? 'gleich' : 'fuehrung' };
  }
  zahlSetzen(k) { this.el(k, 'pk').textContent = standText(this.bstand[k]); }
  strafenSetzen(k) {
    // Drei Punkte; ab der 3. Verwarnung rot (jede gibt einen Minuspunkt,
    // 21.10.2026), ab der 4. steht die Zahl daneben (keine Obergrenze).
    const punkte = [...this.bt.querySelectorAll(`.pt-strafen[data-s="${k}"] i`)];
    punkte.forEach((p, i) => { p.classList.toggle('an', i < this.strafen[k]); p.classList.toggle('rot', this.strafen[k] >= 3 && i < 3); });
    const z = this.bt.querySelector(`.pt-strafen[data-s="${k}"] b`);
    if (z) z.textContent = this.strafen[k] > 3 ? String(this.strafen[k]) : '';
    return punkte;
  }
  minusSetzen(k, mitSprung) {
    const b = this.bt.querySelector(`.pt-minus-zahl[data-m="${k}"]`);
    if (!b) return;
    b.textContent = this.minus[k] ? `−${this.minus[k]}` : '';
    b.classList.toggle('an', this.minus[k] > 0);
    if (mitSprung && this.minus[k]) this.anim(b, [{ transform: 'scale(2)' }, { transform: 'scale(1)' }], { duration: 500, easing: 'cubic-bezier(.2,.8,.2,1)' });
  }

  /** Stand ohne Animation übernehmen (Start, Neuverbindung, Korrektur) */
  uebernehmen(st) {
    for (const k of SEITEN) {
      this.bstand[k] = st[k].stand; this.strafen[k] = st[k].verw; this.minus[k] = st[k].minus;
      this.zahlSetzen(k); this.strafenSetzen(k); this.minusSetzen(k, false);
    }
    const { fuehrt, modus } = this.fuehrtModus();
    this.boben = fuehrt;
    this.bmodus = modus;
    const f = this.bt.querySelector('.pt-bg.pt-fuehrung');
    f.getAnimations().forEach((a) => a.cancel());
    f.style.opacity = modus === 'fuehrung' ? 1 : 0;
    this.aufstellen();
    this.geplant = st;
  }

  // ── Platztausch und Bildwechsel (Muster: bTausch) ────
  tausch(nachOben, modus) {
    const els = [...this.bt.querySelectorAll('.pt-bx[data-k]')];
    const vorher = new Map(els.map((e) => [e, e.getBoundingClientRect()]));
    if (modus !== this.bmodus) {
      const f = this.bt.querySelector('.pt-bg.pt-fuehrung');
      const von = this.bmodus === 'fuehrung' ? 1 : 0, bis = modus === 'fuehrung' ? 1 : 0;
      f.style.opacity = bis;
      this.anim(f, [{ opacity: von }, { opacity: bis }], { duration: 560, easing: 'ease-in-out' });
      this.bmodus = modus;
    }
    this.boben = nachOben;
    this.aufstellen();
    els.forEach((e) => {
      const a = vorher.get(e), n = e.getBoundingClientRect(), hoch = e.dataset.k === nachOben;
      if (!n.width || !n.height) return;
      const dx = a.left - n.left, dy = a.top - n.top, sx = a.width / n.width, sy = a.height / n.height;
      e.style.transformOrigin = '0 0'; e.style.zIndex = hoch ? 3 : 2;
      this.anim(e, [
        { transform: `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})` },
        { transform: `translate(${dx * 0.5 + (hoch ? 1.5 : -1.5) * n.height * 0.15}px, ${dy * 0.5}px) scale(${(sx + 1) / 2 * (hoch ? 1.06 : 0.97)}, ${(sy + 1) / 2 * (hoch ? 1.06 : 0.97)})`, offset: 0.5 },
        { transform: 'none' },
      ], { duration: 560, easing: 'cubic-bezier(.3,.8,.25,1)' });
    });
  }

  // ── Laufzeile „Letzte Wertung“ ───────────────────────
  wertungZeigen(k, n, text) {
    const el = this.bt.querySelector('.pt-info-wertung .pt-iw');
    if (!el) return;
    el.textContent = '';
    const tag = document.createElement('span'); tag.className = `pt-t pt-${k}`; tag.textContent = this.schilder()[k] || TAG[k];
    el.appendChild(tag);
    // Laufzeile kurz halten (Entscheidung 21.10.2026): Technik in Kurzform,
    // bei langen Namen nur der Nachname, damit nichts abgeschnitten wird.
    const was = laufzeileKurz(text || (n < 0 ? 'Minuspunkt' : TECHNIK[n]) || 'Wertung');
    const voll = this.namen()[k] || this.schilder()[k] || TAG[k];
    const name = (voll.length + was.length > 30 && voll.includes(' ')) ? voll.split(' ').slice(-1)[0] : voll;
    const wt = document.createElement('span'); wt.className = 'pt-wt';
    wt.textContent = `${name} · ${was}`;
    el.appendChild(wt);
    if (n) {
      const p = document.createElement('span'); p.className = 'pt-p' + (n < 0 ? ' pt-minus' : '');
      p.textContent = n > 0 ? `+${n}` : `−${-n}`;
      el.appendChild(p);
    }
    this.anim(el, [{ opacity: 0, transform: 'translateX(12%)' }, { opacity: 1, transform: 'none' }], { duration: 450, easing: 'cubic-bezier(.2,.8,.2,1)' });
    this.anim(el.parentElement, [{ boxShadow: 'inset 0 0 0 0 rgba(255,215,110,0)' }, { boxShadow: 'inset 0 0 2cqw .2cqw rgba(255,215,110,.55)' }, { boxShadow: 'inset 0 0 0 0 rgba(255,215,110,0)' }], { duration: 900 });
  }

  // ── Punkt / Minuspunkt (Muster: bPunktAblauf) ────────
  async punktAblauf(k, n, technik) {
    const neu = this.bstand[k] + n, feld = this.el(k, 'pk'), ziel = this.rel(feld);
    const cq = this.cq();
    const g = document.createElement('div');
    g.className = 'pt-wisch' + (n < 0 ? ' pt-rot' : '');
    g.textContent = n < 0 ? '−' + Math.abs(n) : standText(neu);
    g.style.fontSize = (ziel.h * 3.2) + 'px';
    this.bfx.appendChild(g);
    const gw = g.getBoundingClientRect();
    const zx = ziel.x + ziel.w / 2 - gw.width / 2, zy = ziel.y + ziel.h / 2 - gw.height / 2;
    const sx = zx - 40 * cq, sy = zy - 22 * cq;
    const spur = document.createElement('div'); spur.className = 'pt-spur' + (n < 0 ? ' pt-rot' : ''); this.bfx.appendChild(spur);
    const winkel = Math.atan2(zy - sy, zx - sx) * 180 / Math.PI;
    Object.assign(spur.style, { height: 2.4 * cq + 'px', left: (zx + gw.width / 2 - 46 * cq) + 'px', top: (zy + gw.height / 2 - 1.2 * cq) + 'px', width: 46 * cq + 'px' });
    this.anim(spur, [{ transform: `rotate(${winkel}deg) scaleX(0)`, opacity: 0 }, { transform: `rotate(${winkel}deg) scaleX(1)`, opacity: 1, offset: 0.55 }, { transform: `rotate(${winkel}deg) scaleX(.3) translateX(10%)`, opacity: 0 }], { duration: 520, easing: 'cubic-bezier(.2,.7,.2,1)', fill: 'both' });
    if (n < 0) {
      // Minuspunkt: fliegt ein, bleibt groß VOR dem Punktefeld stehen, dann sinkt der Stand
      this.anim(g, [
        { transform: `translate(${sx}px, ${sy}px) rotate(-14deg) scale(1.2) skewX(-12deg)`, opacity: 0 },
        { opacity: 1, offset: 0.12 },
        { transform: `translate(${zx}px, ${zy}px) rotate(0deg) scale(.62) skewX(0)`, opacity: 1, offset: 0.35 },
        { transform: `translate(${zx}px, ${zy}px) scale(.58)`, opacity: 1, offset: 0.8 },
        { transform: `translate(${zx}px, ${zy + ziel.h * 0.5}px) scale(.4)`, opacity: 0 },
      ], { duration: 1500, easing: 'cubic-bezier(.3,.6,.2,1)', fill: 'both' });
      await this.warte(1050);
    } else {
      // Goldene Zahl fegt schräg von oben links herein
      this.anim(g, [
        { transform: `translate(${sx}px, ${sy}px) rotate(-14deg) scale(1.25) skewX(-12deg)`, opacity: 0, filter: 'blur(6px)' },
        { opacity: 1, offset: 0.15 },
        { transform: `translate(${zx}px, ${zy}px) rotate(0deg) scale(.32) skewX(0)`, opacity: 1, filter: 'blur(0)', offset: 0.7 },
        { transform: `translate(${zx}px, ${zy}px) scale(.2)`, opacity: 0 },
      ], { duration: 560, easing: 'cubic-bezier(.3,.6,.2,1)', fill: 'both' });
      await this.warte(390);
    }
    this.bstand[k] = neu; this.zahlSetzen(k);
    if (n < 0) {
      this.minus[k] += -n; this.minusSetzen(k, true);
      this.anim(feld, [{ color: '#ff6b6b', transform: 'scale(1.4) translateY(8%)' }, { color: '#fff', transform: 'none' }], { duration: 800 });
    }
    this.wertungZeigen(k, n, n > 0 ? technik : null);
    const b = document.createElement('div'); b.className = 'pt-blitz'; this.bfx.appendChild(b);
    const bg = ziel.h * 2.6;
    Object.assign(b.style, { width: bg + 'px', height: bg + 'px', left: (ziel.x + ziel.w / 2 - bg / 2) + 'px', top: (ziel.y + ziel.h / 2 - bg / 2) + 'px' });
    this.anim(b, [{ transform: 'scale(.3)', opacity: 1 }, { transform: 'scale(1.2)', opacity: 0 }], { duration: 520, easing: 'ease-out', fill: 'both' });
    this.anim(feld, [{ transform: 'scale(1.5)', color: '#fff3c4' }, { transform: 'scale(1)', color: '#fff' }], { duration: 650, easing: 'cubic-bezier(.2,.8,.2,1)' });
    const { fuehrt, modus } = this.fuehrtModus();
    if (fuehrt !== this.boben || modus !== this.bmodus) { await this.warte(120); this.tausch(fuehrt, modus); }
    await this.warte(600);
    const r = this.rel(this.el(k, 'name'));
    const box = document.createElement('div');
    Object.assign(box.style, { position: 'absolute', left: r.x + 'px', top: r.y + 'px', width: r.w + 'px', height: r.h + 'px', overflow: 'hidden' });
    const band = document.createElement('div'); band.className = 'pt-glanz-band'; box.appendChild(band); this.bfx.appendChild(box);
    await this.anim(band, [{ left: '-40%' }, { left: '120%' }], { duration: 600, easing: 'ease-in-out', fill: 'both' }).finished;
    this.aufraeumen();
  }

  // Effekte weg, das Schild (Unentschieden) und das Siegerbild bleiben
  aufraeumen() {
    [...this.bfx.children].forEach((c) => { if (c !== this.schild && !c.dataset.bleibt) c.remove(); });
  }

  // ── Bänder über dem Namensfeld (Muster: band) ────────
  async band(k, text, art) {
    const r = this.rel(this.el(k, 'name'));
    const b = document.createElement('div'); b.className = 'pt-warnband pt-' + art; b.textContent = text;
    Object.assign(b.style, { left: r.x - r.w * 0.03 + 'px', top: r.y + r.h * 0.08 + 'px', width: r.w * 1.06 + 'px', height: r.h * 0.84 + 'px', fontSize: r.h * 0.3 + 'px' });
    this.bfx.appendChild(b);
    await this.anim(b, [
      { opacity: 0, transform: 'translateX(-30%) skewX(-18deg) scaleX(.6)' },
      { opacity: 1, transform: 'translateX(0) skewX(-10deg) scaleX(1)', offset: 0.2 },
      { opacity: 1, transform: 'translateX(2%) skewX(-10deg) scaleX(1)', offset: 0.8 },
      { opacity: 0, transform: 'translateX(25%) skewX(-18deg) scaleX(.8)' },
    ], { duration: 1500, easing: 'cubic-bezier(.2,.7,.2,1)', fill: 'both' }).finished;
    b.remove();
  }

  async verwarnungAblauf(k, mitMinus) {
    this.strafen[k] += 1;
    const punkte = this.strafenSetzen(k);
    const neu = this.strafen[k] > 3 ? this.bt.querySelector(`.pt-strafen[data-s="${k}"] b`) : punkte[this.strafen[k] - 1];
    if (neu) this.anim(neu, [{ transform: 'scale(2.2)' }, { transform: 'scale(1)' }], { duration: 500, easing: 'cubic-bezier(.2,.8,.2,1)' });
    // Mit Minuspunkt: „Verwarnung 3“ und rotes „−1“ dahinter (das −1 kürzt
    // nie). Vorher „Verwarnung 3 · Minuspunkt“, in der Laufzeile „Minu…“.
    this.wertungZeigen(k, mitMinus ? -1 : 0, `Verwarnung ${this.strafen[k]}`);
    await this.band(k, `Verwarnung ${this.strafen[k]}`, 'gelb');
  }

  async minusAblauf(k) {
    await this.band(k, 'Minuspunkt', 'rot');
    await this.punktAblauf(k, -1);
  }

  // ── Neuer Stand vom Server ───────────────────────────
  neuerStand(st) {
    if (!this.geplant) { this.uebernehmen(st); return; }
    const alt = this.geplant;
    this.geplant = st;
    this.schildWeg();
    for (const k of SEITEN) {
      const o = alt[k], n = st[k];
      const dw = (n.stand + n.minus) - (o.stand + o.minus);
      const dv = n.verw - o.verw;
      const dm = n.minus - o.minus;
      if (!dw && !dv && !dm) continue;
      if (dw < 0 || dv < 0 || dm < 0) {
        // Rücknahme durch den Hauptkampfrichter: ohne Feuerwerk übernehmen
        this.einreihen(() => this.korrektur(k, st));
        continue;
      }
      for (let i = 0; i < dv; i++) {
        const letzte = i === dv - 1;
        this.einreihen(() => this.verwarnungAblauf(k, letzte && dm > 0 && n.verw >= 3));
      }
      for (let i = 0; i < dm; i++) this.einreihen(() => this.minusAblauf(k));
      // Technik aus dem Knopf nur, wenn genau diese Wertung neu ist (nr) und
      // zur Seite und Punktzahl passt
      const lw = st.lw;
      const technik = lw && lw.farbe === k && lw.punkte === dw && lw.nr !== (alt.lw && alt.lw.nr) ? lw.technik : null;
      if (dw > 0) this.einreihen(() => this.punktAblauf(k, dw, technik));
    }
    // Zum Schluss sicherstellen, dass genau DIESER Serverstand steht. Nicht
    // this.geplant: das ist schon der nächste Stand, wenn während der
    // Animation weitere Wertungen kamen; dann liefen dessen Abläufe noch
    // einmal obendrauf (21.10.2026: 4. Verwarnung zeigte kurz 5 und −3).
    this.einreihen(() => this.abgleichen(st));
  }

  async korrektur(k, ziel) {
    const st = ziel || this.geplant;
    this.bstand[k] = st[k].stand; this.strafen[k] = st[k].verw; this.minus[k] = st[k].minus;
    this.zahlSetzen(k); this.strafenSetzen(k); this.minusSetzen(k, false);
    this.wertungZeigen(k, 0, 'Wertung zurückgenommen');
    this.anim(this.el(k, 'pk'), [{ opacity: 0.2 }, { opacity: 1 }], { duration: 400 });
    const { fuehrt, modus } = this.fuehrtModus();
    if (fuehrt !== this.boben || modus !== this.bmodus) this.tausch(fuehrt, modus);
    await this.warte(600);
  }

  abgleichen(ziel) {
    const st = ziel || this.geplant;
    let anders = false;
    for (const k of SEITEN) {
      if (this.bstand[k] !== st[k].stand || this.minus[k] !== st[k].minus || this.strafen[k] !== st[k].verw) anders = true;
    }
    if (!anders) return;
    for (const k of SEITEN) {
      this.bstand[k] = st[k].stand; this.strafen[k] = st[k].verw; this.minus[k] = st[k].minus;
      this.zahlSetzen(k); this.strafenSetzen(k); this.minusSetzen(k, false);
    }
    const { fuehrt, modus } = this.fuehrtModus();
    if (fuehrt !== this.boben || modus !== this.bmodus) this.tausch(fuehrt, modus);
  }

  // ── Kampfzeit vorbei bei Gleichstand (Muster: unentschieden) ──
  unentschieden() {
    return this.einreihen(async () => {
      if (this.bstand.rot !== this.bstand.blau || this.schild) return;
      for (const k of SEITEN) for (const t of ['foto', 'name', 'pk']) {
        this.anim(this.el(k, t), [{ filter: 'brightness(1)' }, { filter: 'brightness(1.5)' }, { filter: 'brightness(1)' }], { duration: 900 });
      }
      const s = document.createElement('div'); s.className = 'pt-sieger-schild'; s.textContent = 'Unentschieden';
      Object.assign(s.style, { fontSize: '4.8cqw', top: '86%', left: '32%' });
      this.bfx.appendChild(s); this.schildSetzen(s);
      this.anim(s, [{ opacity: 0, transform: 'translate(-90%,-50%) skewX(-20deg) scale(1.5)', filter: 'blur(8px)' }, { opacity: 1, transform: 'translate(-50%,-50%) skewX(-6deg) scale(1)', filter: 'blur(0)', offset: 0.6 }, { opacity: 1, transform: 'translate(-50%,-50%) skewX(-6deg) scale(1)' }], { duration: 700, easing: 'cubic-bezier(.2,.7,.2,1)', fill: 'forwards' });
      await this.warte(1400);
    });
  }

  /** Hauptkampfrichter gibt Verlängerung (Server zählt): Schild wechselt zu „Verlängerung“ bzw. „Goldener Punkt“. */
  verlaengerung(uhr, text = 'Verlängerung') {
    return this.einreihen(async () => {
      const s = this.schild;
      if (!s) return;
      await this.anim(s, [{ opacity: 1, transform: 'translate(-50%,-50%) skewX(-6deg) scale(1)' }, { opacity: 0, transform: 'translate(-50%,-50%) skewX(-6deg) scale(.9) rotateX(90deg)' }], { duration: 300, fill: 'forwards' }).finished;
      s.textContent = text;
      this.anim(s, [{ opacity: 0, transform: 'translate(-50%,-50%) skewX(-6deg) rotateX(-90deg)' }, { opacity: 1, transform: 'translate(-50%,-50%) skewX(-6deg) rotateX(0)' }], { duration: 360, fill: 'forwards' });
      if (uhr) this.anim(uhr, [{ transform: 'scale(1.3)', color: '#fff3c4', textShadow: '0 0 1cqw rgba(255,215,110,1)' }, { transform: 'scale(1)', color: '#fff', textShadow: 'none' }], { duration: 800, easing: 'cubic-bezier(.2,.8,.2,1)' });
      await this.warte(1600);
      await this.anim(s, [{ opacity: 1 }, { opacity: 0 }], { duration: 400, fill: 'forwards' }).finished;
      s.remove(); if (this.schild === s) this.schildSetzen(null);
    });
  }

  /** Verlängerung vorbei, Gleichstand (F5): Schild „Unentschieden“ wird „Kampfrichterentscheid“. */
  entscheid() {
    return this.einreihen(async () => {
      if (this.bstand.rot !== this.bstand.blau) return;
      const text = 'Kampfrichterentscheid';
      let s = this.schild;
      if (!s) {
        s = document.createElement('div'); s.className = 'pt-sieger-schild'; s.textContent = text;
        Object.assign(s.style, { fontSize: '3.4cqw', top: '86%', left: '32%' });
        this.bfx.appendChild(s); this.schildSetzen(s);
        this.anim(s, [{ opacity: 0, transform: 'translate(-50%,-50%) skewX(-6deg) scale(1.3)' }, { opacity: 1, transform: 'translate(-50%,-50%) skewX(-6deg) scale(1)' }], { duration: 600, fill: 'forwards' });
        return;
      }
      if (s.textContent === text) return;
      await this.anim(s, [{ opacity: 1, transform: 'translate(-50%,-50%) skewX(-6deg) scale(1)' }, { opacity: 0, transform: 'translate(-50%,-50%) skewX(-6deg) scale(.9) rotateX(90deg)' }], { duration: 300, fill: 'forwards' }).finished;
      s.textContent = text; s.style.fontSize = '3.4cqw';
      this.anim(s, [{ opacity: 0, transform: 'translate(-50%,-50%) skewX(-6deg) rotateX(-90deg)' }, { opacity: 1, transform: 'translate(-50%,-50%) skewX(-6deg) rotateX(0)' }], { duration: 360, fill: 'forwards' });
    });
  }

  /** Schild („Unentschieden“, „Verlängerung“, „Goldener Punkt“,
      „Kampfrichterentscheid“) steht auf dem Platz der beiden Infozeilen
      unten: solange es da ist, treten „Letzte Wertung“ und „Als Nächstes“
      zurück (pt-mit-schild, 25.10.2026; vorher überdeckte der Schriftzug sie). */
  schildSetzen(s) {
    this.schild = s;
    this.bt.classList.toggle('pt-mit-schild', !!s);
  }

  schildWeg() {
    const s = this.schild;
    if (!s) return;
    this.schildSetzen(null);
    this.anim(s, [{ opacity: 1 }, { opacity: 0 }], { duration: 400, fill: 'forwards' }).finished.then(() => s.remove(), () => s.remove());
  }

  // ── Siegerbild Variante A (Entscheidung 20.10.2026) ──
  sieger(sieg) {
    if (this.siegerGezeigt || !SEITEN.includes(sieg)) return this.lauf;
    this.siegerGezeigt = true;
    return this.einreihen(async () => {
      this.schildWeg();
      // Sieger nach Entscheidung bei Gleichstand: erst nach oben
      if (this.boben !== sieg) { this.tausch(sieg, this.bmodus); await this.warte(600); }
      const verl = andere(sieg);
      const aus = (el, bis = 0) => el && this.anim(el, [{ opacity: getComputedStyle(el).opacity }, { opacity: bis }], { duration: 700, fill: 'forwards', easing: 'ease-in' });
      ['foto', 'name', 'pk'].forEach((t) => this.anim(this.el(verl, t), [{ opacity: 1, filter: 'none' }, { opacity: 0.18, filter: 'grayscale(1)' }], { duration: 700, fill: 'forwards' }));
      this.bt.querySelectorAll('.pt-info-wertung, .pt-info-naechster').forEach((e) => aus(e));
      const st = document.createElement('div'); st.className = 'pt-strahlen'; st.dataset.bleibt = '1'; this.bfx.appendChild(st);
      const fz = this.rel(this.el(sieg, 'foto')), nz = this.rel(this.el(sieg, 'name')), pz = this.rel(this.el(sieg, 'pk'));
      const cq = this.cq();
      Object.assign(st.style, { left: (fz.x + (pz.x + pz.w - fz.x) / 2) + 'px', top: (nz.y + nz.h / 2) + 'px' });
      this.anim(st, [{ opacity: 0, transform: 'translate(-50%,-50%) rotate(0deg) scale(.6)' }, { opacity: 1, transform: 'translate(-50%,-50%) rotate(25deg) scale(1)' }], { duration: 2600, easing: 'ease-out', fill: 'forwards' });
      this.anim(this.el(sieg, 'foto'), [{ boxShadow: '0 0 0 0 rgba(255,215,110,.9)' }, { boxShadow: '0 0 3cqw 1cqw rgba(255,215,110,.7)' }, { boxShadow: '0 0 1.6cqw .3cqw rgba(255,215,110,.5)' }], { duration: 1400, fill: 'forwards' });
      this.anim(this.el(sieg, 'name'), [{ filter: 'brightness(1)' }, { filter: 'brightness(1.35)' }, { filter: 'brightness(1.12)' }], { duration: 1400, fill: 'forwards' });
      await this.warte(450);
      // Goldenes Band direkt unter dem Siegerfeld
      const b = document.createElement('div'); b.className = 'pt-sieger-band'; b.dataset.bleibt = '1';
      const sp = document.createElement('span'); sp.textContent = 'Sieger'; b.appendChild(sp);
      Object.assign(b.style, { left: fz.x + 'px', top: (nz.y + nz.h + cq * 0.8) + 'px', width: (pz.x + pz.w - fz.x) + 'px', height: cq * 5 + 'px', fontSize: cq * 3.6 + 'px' });
      this.bfx.appendChild(b);
      this.anim(b, [{ opacity: 0, transform: 'scaleX(0)' }, { opacity: 1, transform: 'scaleX(1.03)', offset: 0.6 }, { opacity: 1, transform: 'scaleX(1)' }], { duration: 700, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'forwards' });
      const g = document.createElement('div'); g.className = 'pt-glanz-band'; b.appendChild(g);
      this.anim(g, [{ left: '-40%' }, { left: '130%' }], { duration: 900, delay: 500, easing: 'ease-in-out', fill: 'both' });
    });
  }
}

// ════════════════════════════════════════════════════════════════════════
//  Komponente
// ════════════════════════════════════════════════════════════════════════
const formatZeit = (s) => `${Math.floor(Math.max(0, s) / 60)}:${String(Math.floor(Math.max(0, s)) % 60).padStart(2, '0')}`;

function Bild({ seite, info, style }) {
  const art = info?.bild_art || 'logo';
  return (
    <div className={`pt-bx pt-foto pt-${seite} pt-bild-${art}`} data-k={seite} data-t="foto"
      // Foto (nur mit Hallenschlüssel, pages/MatDisplay.jsx) mit dem Vereinslogo
      // darunter: lädt das Foto nicht (Einwilligung widerrufen, Schlüssel neu),
      // steht das Logo da statt eines leeren Felds.
      style={{ ...style, backgroundImage: [info?.bild || '/logo.png', info?.ersatz].filter(Boolean).map((u) => `url("${u}")`).join(', ') }}>
      <div className="pt-ecke" />
    </div>
  );
}

/**
 * @param {object}  props.s          Mattenzustand (mat-state + score-update)
 * @param {number}  props.matNr
 * @param {number}  props.uhrEnde    zählt hoch, wenn die Kampfzeit abgelaufen ist (timer-ended)
 * @param {object} [props.farben]    Seitenfarben aus der Modus-Übersicht (lib/seitenFarben.js
 *                                   tafelFarben); ohne: die festen Tafelfarben
 */
export default function Punktetafel({ s, matNr, uhrEnde, farben }) {
  const btRef = useRef(null);
  const uhrRef = useRef(null);
  const ablaufRef = useRef(null);
  const fi = s.fight_info || {};
  const tafel = s.tafel || null;
  const namenRef = useRef({ rot: fi.rot || '', blau: fi.blau || '' });
  namenRef.current = { rot: fi.rot || '', blau: fi.blau || '' };
  // Schilder ROT/BLAU oder die Beschriftung aus der Modus-Übersicht (lib/seitenFarben.js)
  const schilder = { rot: farben?.rotName || TAG.rot, blau: farben?.blauName || TAG.blau };
  const schilderRef = useRef(schilder);
  schilderRef.current = schilder;
  // Verlängerung zählt der Server (Kampfrichter-App, Knopf „Verlängerung“,
  // backend/utils/verlaengerung.js) seit 21.10.2026.
  const verlaengerung = Number(s.verlaengerung) || 0;
  const verlaengerungRef = useRef(verlaengerung);
  // Goldener Punkt und Kampfrichterentscheid (23.10.2026, F5/F6): setzt der
  // Server nur für Kämpfe mit geladenen Regeln
  const goldenPoint = !!s.golden_point;
  const entscheid = !!s.kampfrichterentscheid;

  // Ablauf einmal je Kampf (MatDisplay setzt key auf den Kampf)
  useLayoutEffect(() => {
    const a = new TafelAblauf(btRef.current, () => namenRef.current, () => schilderRef.current);
    ablaufRef.current = a;
    return () => a.beenden();
  }, []);

  // Stand → Animationen
  const st = standAus(s);
  const standSchluessel = JSON.stringify(st);
  useEffect(() => {
    const a = ablaufRef.current;
    if (!a) return;
    a.neuerStand(JSON.parse(standSchluessel));
  }, [standSchluessel]);

  // Kampfzeit vorbei bei Gleichstand → „Unentschieden“
  const gleich = st.rot.stand === st.blau.stand;
  const beendet = s.status === 'finished';
  useEffect(() => {
    if (!uhrEnde || beendet || !gleich) return;
    ablaufRef.current?.unentschieden();
    // nur auf das Ereignis, nicht auf Stand-Änderungen
  }, [uhrEnde]); // eslint-disable-line react-hooks/exhaustive-deps

  // Neue Verlängerung vom Hauptkampfrichter: Band „Unentschieden“ wechselt
  // zu „Verlängerung“ (Muster). Kam der Bildschirm erst danach dazu, steht nur
  // die Zeile unter der Uhr.
  useEffect(() => {
    const vorher = verlaengerungRef.current;
    verlaengerungRef.current = verlaengerung;
    if (!(verlaengerung > vorher)) return;
    const a = ablaufRef.current;
    if (!a) return;
    a.unentschieden();
    a.verlaengerung(uhrRef.current, goldenPoint ? 'Goldener Punkt' : 'Verlängerung');
  // goldenPoint kommt mit demselben Stand wie der Zähler (utils/verlaengerung.anwenden)
  }, [verlaengerung]); // eslint-disable-line react-hooks/exhaustive-deps

  // Kampfrichterentscheid (F5): nach dem Schild „Unentschieden“ bzw. beim
  // Beitritt, wenn der Server ihn schon gesetzt hat
  useEffect(() => {
    if (entscheid && gleich && !beendet) ablaufRef.current?.entscheid();
    // nur auf den Vermerk, nicht auf Stand-Änderungen
  }, [entscheid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ergebnis bestätigt → Siegerbild Variante A
  useEffect(() => {
    if (beendet && (s.winner === 'rot' || s.winner === 'blau')) ablaufRef.current?.sieger(s.winner);
  }, [beendet, s.winner]);

  const runde = tafel?.runde || 'Kampf';
  const rundeGroesse = runde.length > 12 ? '3.8cqw' : runde.length > 8 ? '4.6cqw' : undefined;
  const kat = tafel?.kategorie || fi.division_name || fi.division_code || '';
  const katLang = kat.length > 22;
  const turnierName = tafel?.turnier?.name || fi.turnier_name || '';
  const n = tafel?.naechster;
  const uhrText = goldenPoint ? 'Goldener Punkt' : verlaengerung ? 'Verlängerung' : 'Kampfzeit';
  const uhrZeile = beendet ? 'Beendet'
    : s.golden_point_sieger ? 'Punkt erzielt'
    : entscheid && gleich ? 'Kampfrichterentscheid'
    : goldenPoint ? 'Erster Punkt entscheidet'
    : verlaengerung ? `${verlaengerung}. Verlängerung`
    : s.timer_running ? 'Läuft'
    // Der Mattenbildschirm bekommt beim Start nur timer-update, kein neues
    // status — darum an der Restzeit ablesen
    : Number(s.timer_remaining) <= 0 ? 'Zeit abgelaufen'
    : (s.status === 'active' || Number(s.timer_remaining) < Number(s.timer_seconds)) ? 'Pause' : 'Bereit';

  return (
    <div className="pt-wurzel" data-punktetafel={matNr} style={tafelVariablen(farben)}>
      <div className="pt-tafel" ref={btRef}>
        <div className="pt-bg pt-gleich" style={{ backgroundImage: `url("${BILDER}/tafel_gleich.jpg")` }} />
        <div className="pt-bg pt-fuehrung" style={{ backgroundImage: `url("${BILDER}/tafel_a.jpg")` }} />
        {/* Breiter als im Muster (46 %): Kategorie mit Alter, Geschlecht und Gewicht braucht Platz bis vor das Turnierlogo */}
        <div className="pt-bx pt-titel" style={{ left: '16.5%', top: '6.5%', width: '52%', height: '13%' }}>
          <div className="pt-t" key={runde} style={{ fontSize: rundeGroesse }} data-runde={runde}>{runde}</div>
          <div className={`pt-k${katLang ? ' pt-lang' : ''}`}>
            {/* lang (Alter · Geschlecht · Gewicht): Disziplin und Matte in einer Zeile,
                die Kategorie darunter auf bis zu zwei Zeilen (vorher abgeschnitten:
                „18–34 Jahre · männlic…“). Sonst wie im Muster „Kategorie · Matte N“. */}
            {katLang
              ? <><span>{[tafel?.disziplin || 'Kampf', `Matte ${matNr}`].join(' · ')}</span><span className="pt-kat">{kat}</span></>
              : <><span>{tafel?.disziplin || 'Kampf'}</span><span>{[kat, `Matte ${matNr}`].filter(Boolean).join(' · ')}</span></>}
          </div>
        </div>
        <div className="pt-bx pt-uhr" style={{ left: '64.9%', top: '21.3%', width: '28.6%', height: '56.3%' }}>
          <div className="pt-u" ref={uhrRef}>{formatZeit(s.timer_remaining)}</div>
          <div className="pt-ut">{uhrText}</div>
          <div className="pt-um">{uhrZeile}</div>
        </div>
        {SEITEN.map((k) => (
          <React.Fragment key={k}>
            <Bild seite={k} info={tafel?.[k]} />
            <div className={`pt-bx pt-name pt-${k}`} data-k={k} data-t="name">
              <div className="pt-nm">{namenRef.current[k] || ' '}</div>
              <div className="pt-minus-zahl" data-m={k} />
              <div className="pt-info pt-strafen" data-s={k}><i /><i /><i /><b /></div>
              <div className="pt-vn"><span className="pt-seite-tag">{schilder[k]}</span>{tafel?.[k]?.verein || ''}</div>
            </div>
            <div className={`pt-bx pt-pk pt-${k}`} data-k={k} data-t="pk" />
          </React.Fragment>
        ))}
        <div className="pt-bx pt-info pt-info-logo" style={{ left: '69.38%', top: '7.44%', width: '23.92%', height: '12.22%' }}>
          <img src={tafel?.turnier?.logo || '/logo.png'} alt="" />
          <div><b className={turnierName.length > 16 ? 'pt-lang' : undefined}>{turnierName}</b>{tafel?.turnier?.veranstalter && <span>Veranstalter: {tafel.turnier.veranstalter}</span>}</div>
        </div>
        <div className="pt-bx pt-info pt-info-wertung" style={{ left: '6.64%', top: '78.43%', width: '56.04%', height: '5.74%' }}>
          <span className="pt-il">Letzte Wertung</span><span className="pt-iw">–</span>
        </div>
        {n && (
          <div className="pt-bx pt-info pt-info-naechster" style={{ left: '6.64%', top: '85.23%', width: '56.04%', height: '4.68%' }}>
            <span className="pt-il">Als Nächstes · Matte {matNr}</span>
            <span className="pt-in">{[n.kategorie, n.runde].filter(Boolean).join(' · ')}</span>
            <span className="pt-ip">{n.rot} – {n.blau}</span>
          </div>
        )}
        {/* Automatische Disqualifikation (21.10.2026, Einstellung je
            Divisionstyp/Turnier): rotes Band über den Infozeilen, bis der
            Hauptkampfrichter das Ergebnis bestätigt oder die Verwarnung
            zurücknimmt. */}
        {s.auto_dq && !beendet && (
          <div className="pt-warnband pt-rot pt-dq-band" data-auto-dq={s.auto_dq.farbe}
            style={{ left: '4%', top: '78.4%', width: '61.5%', height: '11.5%' }}>
            {autoDqText(s.auto_dq)}
          </div>
        )}
        <div className="pt-bfx" />
      </div>
    </div>
  );
}
